import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { cn, focusRing, targetSize } from '@/shared/ui/cn'

/**
 * The line above a search's results, from the mockups' `find-search/ResultsSummary.jsx`: the count,
 * then, while the viewer has chosen one, the sort with Clear sort. The count sits in an `output`,
 * so a screen reader hears each change; Clear sort leaves with its button, so focus goes to the line.
 */
export function ResultsSummary(props: {
  /** Such as "Sorted by Name (ascending order)"; `null` while the screen's own order applies. */
  readonly sortedBy: string | null
  readonly onClearSort: () => void
  readonly className?: string | false | undefined
  /** The `output`'s content: the count, and any loading or refusal for a screen reader. */
  readonly children: ReactNode
}) {
  const intl = useIntl()
  const { sortedBy, onClearSort } = props
  const lineRef = useRef<HTMLDivElement>(null)
  const isFocusPending = useRef(false)
  const clear = useCallback(() => {
    isFocusPending.current = true
    onClearSort()
  }, [onClearSort])
  useEffect(() => {
    if (isFocusPending.current && sortedBy === null) {
      isFocusPending.current = false
      lineRef.current?.focus()
    }
  }, [sortedBy])

  return (
    <div ref={lineRef} tabIndex={-1} className={cn('mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-text-secondary', props.className)}>
      <output>{props.children}</output>
      {sortedBy !== null && (
        <>
          <span aria-hidden="true">·</span>
          <span>{sortedBy}</span>
          <button type="button" onClick={clear} className={cn('cursor-pointer rounded font-medium text-primary-main hover:underline', targetSize, focusRing)}>
            {intl.formatMessage({ id: 'search.sort.clear' })}
          </button>
        </>
      )}
    </div>
  )
}
